import { useState, useEffect, useRef } from "react";
import {
  DialogProps,
  ChannelInfo,
  TurbineInfo,
} from "../../types/dialog-types";
import { getEventModelTimes } from "../../api/getEventModelTimes";
import { useTurbineSerials } from "../../utils/turbineSerial";
import { ListControl } from "../common/Dialog";
import type { SeriesSpec } from "../../types/multi-time-series";
import { tigerSecondsToDate, formatTigerDateTime, formatDurationSecs, TIGER_TO_UNIX_OFFSET_SECS, validateEndAfterStart, validateMaxDuration } from "../../utils/tigerTime";
import { useTigerMaxReplay } from "../../utils/tigerMaxReplay";
import { fetchTurbineList, fetchChannelList, fetchTurbineStates, type GraphChannelMode } from "../../api/channelSelection";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";

// Re-exported for existing importers of this module.
export type { GraphChannelMode };

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

const styles = {
  ...acrylicStyles,
  // Initial offsets centre a 620 × 720 dialog; once the user resizes, it grows
  // from this anchor toward the bottom-right (see acrylicDialogSurface).
  dialog: acrylicDialogSurface({ width: 620, height: 720, minWidth: 480, minHeight: 640 }),
} as const;

export interface GraphChannelSelectionDialogProps extends DialogProps {
  /** Header text. Defaults to "Graph Channel Selection". */
  title?: string;
  /** Channel store to draw the list from:
   *   - "live"  → `get_channel_list` (default)
   *   - "trend" → `get_trend_channel_list`
   *   - "event" → `get_event_channel_list`
   */
  mode?: GraphChannelMode;
  /** Called instead of onApply when mode === "multiTime". */
  onApplySeries?: (spec: SeriesSpec) => void;
  /** When set in multiTime mode, preloads the time panel and preserves id/colour on Apply. */
  editingSeries?: SeriesSpec;
  /** Hide the Turbine State selector. Used by the Data Grid window where the
   *  displayed state is the turbine's live state, not a per-row filter. */
  hideTurbineState?: boolean;
  /** Config to preselect the first time the dialog opens. Set by the graph
   *  window when every plotted series shares one config, so adding more tags
   *  starts on that turbine instead of the first in the list. Ignored if it
   *  isn't in the turbine list, or on subsequent opens. */
  defaultConfig?: string;
}

export function GraphChannelSelectionDialog({
  open,
  onClose,
  onApply,
  onApplySeries,
  title = getTigerResource("TIGER_GRAPH_CHANNEL_GRAPH_TITLE"),
  mode = "live",
  editingSeries,
  hideTurbineState = false,
  defaultConfig,
}: GraphChannelSelectionDialogProps) {
  const [turbines, setTurbines] = useState<TurbineInfo[]>([]);
  const [turbine, setTurbine] = useState("");
  const [turbineState, setTurbineState] = useState("");
  const [turbineStates, setTurbineStates] = useState<string[]>([]);

  const [channelType, setChannelType] = useState("Analogue");
  const [tagPattern, setTagPattern] = useState("");
  const [descriptionPattern, setDescriptionPattern] = useState("");
  const [allChannels, setAllChannels] = useState<ChannelInfo[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  const isMultiTime = mode === "multiTime";
  const [timeMode, setTimeMode] = useState<"current" | "historical">("current");
  const [eventModel, setEventModel] = useState<string>("");
  const [eventModels, setEventModels] = useState<Array<[string, string]>>([]);
  const [occurrences, setOccurrences] = useState<Array<{ start: number; end: number }>>([]);
  const [occurrencesLoading, setOccurrencesLoading] = useState<boolean>(false);
  const [occurrencesError, setOccurrencesError] = useState<string>("");
  const [selectedOccurrenceIndex, setSelectedOccurrenceIndex] = useState<number | null>(null);
  // User-entered Time-mode ranges, persisted across event-dropdown switches
  // and across multi-time graph windows via localStorage.
  const USER_TIME_RANGES_KEY = "tiger.multiTime.userTimeRanges";
  const [userTimeRanges, setUserTimeRanges] = useState<Array<{ start: number; end: number }>>(
    () => {
      if (typeof window === "undefined") return [];
      try {
        const raw = window.localStorage.getItem(USER_TIME_RANGES_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
          (x): x is { start: number; end: number } =>
            x != null && typeof x.start === "number" && typeof x.end === "number"
        );
      } catch {
        return [];
      }
    },
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(USER_TIME_RANGES_KEY, JSON.stringify(userTimeRanges));
    } catch {
      // localStorage may be unavailable (e.g. private mode); ignore.
    }
  }, [userTimeRanges]);
  // Whichever list the occurrences ListControl shows depends on the dropdown:
  // an event model → its backend-fetched occurrences, "Time" → the user's
  // saved manual ranges.
  const displayedOccurrences = eventModel === "" ? userTimeRanges : occurrences;
  // Serial-tracking sites show the engine serial per occurrence instead of
  // Duration (C++ graph_channel_selection swaps the column widths on
  // enable_turbine_serial).
  const { enabled: serialEnabled, serialFor } = useTurbineSerials(turbine, displayedOccurrences);
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const historicalDisabled = !isMultiTime || timeMode === "current";
  const [timeError, setTimeError] = useState<string>("");
  const maxReplay = useTigerMaxReplay();

  // Returns Tiger seconds (1901-01-01 epoch) so SeriesSpec.startTime/endTime
  // are in the same units as the values returned by `getEventModelTimes` and
  // expected by the backend's graph_tags_list parser. The "Z" suffix forces
  // UTC parsing so the round-trip via `tigerSecondsToDate` (which uses UTC)
  // is loss-free.
  function parseDateTime(date: string, time: string): number {
    if (!date || !time) return NaN;
    const ms = Date.parse(`${date}T${time}Z`);
    if (Number.isNaN(ms)) return NaN;
    return Math.floor(ms / 1000) + TIGER_TO_UNIX_OFFSET_SECS;
  }

  function buildSpec(channel: ChannelInfo, configName: string): SeriesSpec | null {
    if (!isMultiTime) return null;
    const id = editingSeries?.id
      ?? ((globalThis.crypto && "randomUUID" in globalThis.crypto)
           ? globalThis.crypto.randomUUID()
           : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const tagName = `${configName}:${channel.tag}`;
    const turbineName =
      turbines.find((t) => t.config_name === configName)?.turbine_name ?? configName;
    const eventModelName =
      eventModels.find(([id]) => id === eventModel)?.[1] || eventModel;
    const description = channel.description ?? "";

    if (timeMode === "current") {
      setTimeError("");
      // CURRENT + an event model selected → backend routes to
      // ModelNormalGraphDataMsg (graph_type=4). CURRENT + Time → plain live
      // DataGraphMsg (graph_type=2). Pass through whichever the dropdown holds.
      return {
        id,
        tagName,
        mode: "CURRENT",
        eventModel,
        relativeIndex: 0,
        startTime: 0,
        endTime: 0,
        colour: editingSeries?.colour ?? "",
        turbineState,
        turbineName,
        eventModelName,
        description,
      };
    }

    const s = parseDateTime(startDate, startTime);
    const e = parseDateTime(endDate, endTime);
    if (!Number.isFinite(s) || !Number.isFinite(e)) {
      setTimeError(getTigerResource("TIGER_TREND_ENTER_TIMES"));
      return null;
    }
    const rangeErr = validateEndAfterStart(s, e);
    if (rangeErr) {
      setTimeError(rangeErr);
      return null;
    }
    const durErr = validateMaxDuration(e - s, maxReplay);
    if (durErr) {
      setTimeError(durErr);
      return null;
    }
    setTimeError("");

    if (eventModel !== "" && selectedOccurrenceIndex != null) {
      return {
        id,
        tagName,
        mode: "RELATIVE",
        eventModel,
        relativeIndex: selectedOccurrenceIndex,
        startTime: s,
        endTime: e,
        colour: editingSeries?.colour ?? "",
        turbineState,
        turbineName,
        eventModelName,
        description,
      };
    }

    return {
      id,
      tagName,
      mode: "HISTORICAL",
      eventModel: "",
      relativeIndex: 0,
      startTime: s,
      endTime: e,
      colour: editingSeries?.colour ?? "",
      turbineState,
      turbineName,
      eventModelName: "",
      description,
    };
  }

  // Tracks whether the dialog has applied its initial turbine selection. The
  // `defaultConfig` preselection only applies the first time the dialog opens.
  const initialConfigAppliedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Honour defaultConfig only on the very first open. A reopen keeps the
    // turbine picked last time, like the channel type and filters (the dialog
    // stays mounted); it used to jump back to the first in the list (Eric,
    // 2026-10-02).
    const applyDefaultConfig = !initialConfigAppliedRef.current;
    initialConfigAppliedRef.current = true;

    fetchTurbineList().then((list) => {
      if (cancelled) return;
      setTurbines(list);
      const listed = (cfg: string | undefined) => cfg != null && cfg !== "" && list.some((t) => t.config_name === cfg);
      setTurbine((current) => {
        if (applyDefaultConfig && listed(defaultConfig)) return defaultConfig as string;
        if (listed(current)) return current;
        return list.length > 0 ? list[0].config_name : current;
      });
    });

    return () => { cancelled = true; };
  }, [open, mode]);

  // Fetch channels on open and when turbine or channelType changes. The dialog
  // stays mounted while closed, so channelType survives a close; fetching here
  // on open keeps the list matching the dropdown.
  useEffect(() => {
    if (!open || !turbine) return;
    let cancelled = false;
    fetchChannelList(turbine, channelType, mode).then((list) => {
      if (!cancelled) {
        setAllChannels(list);
        setSelectedIndices(new Set());
      }
    });
    return () => { cancelled = true; };
  }, [open, turbine, channelType, mode]);

  // Fetch the per-config turbine state list whenever the selected turbine changes.
  // Source: `get_turbine_state_list(config_name, false)` in lib_tiger_rs.
  useEffect(() => {
    if (!open || mode === "event" || !turbine) {
      setTurbineStates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchTurbineStates(turbine);
        if (cancelled) return;
        setTurbineStates(list);
        // Pre-select the first state (DEFAULT, courtesy of
        // get_alphabetic_turbine_state_list). Also handles a stale selection
        // that's no longer in the new list.
        if (list.length === 0) {
          setTurbineState("");
        } else if (!list.includes(turbineState)) {
          setTurbineState(list[0]);
        }
      } catch (e) {
        console.error("get_turbine_states failed:", e);
        if (!cancelled) setTurbineStates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, turbine]);

  // Fetch event-model names when the dialog opens in multiTime mode or the turbine changes.
  useEffect(() => {
    if (!open || !isMultiTime) return;
    let cancelled = false;
    (async () => {
      try {
        if (typeof window !== "undefined" && "__TAURI_INTERNALS__" in window) {
          const { invoke } = await import("@tauri-apps/api/core");
          const list = await invoke<Array<[string, string]>>("get_event_models", { configName: turbine });
          if (!cancelled) setEventModels(list);
        } else {
          const res = await fetch(`/api/get_event_models?config_name=${encodeURIComponent(turbine)}`);
          const raw = (await res.json()) as unknown;
          // Accept both the new [id, name] tuple shape and the legacy string[] shape for resilience.
          const list: Array<[string, string]> = Array.isArray(raw)
            ? raw.map((item) =>
                Array.isArray(item) ? [String(item[0]), String(item[1])] : [String(item), String(item)]
              )
            : [];
          if (!cancelled) setEventModels(list);
        }
      } catch (e) {
        console.error("get_event_models failed:", e);
        if (!cancelled) setEventModels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isMultiTime, turbine]);

  // Fetch occurrences when eventModel changes.
  useEffect(() => {
    if (!isMultiTime) return;
    if (eventModel === "") {
      setOccurrences([]);
      setSelectedOccurrenceIndex(null);
      setOccurrencesLoading(false);
      setOccurrencesError("");
      return;
    }
    let cancelled = false;
    setOccurrencesLoading(true);
    setOccurrencesError("");
    getEventModelTimes(turbine, "HIST", eventModel)
      .then((list) => {
        if (!cancelled) {
          setOccurrences(list);
          setOccurrencesLoading(false);
          setSelectedOccurrenceIndex((prev) => {
            // When preloading an edit of a RELATIVE series, the preload effect has
            // already set this to editingSeries.relativeIndex; keep that selection.
            if (
              editingSeries &&
              editingSeries.mode === "RELATIVE" &&
              editingSeries.eventModel === eventModel &&
              prev === editingSeries.relativeIndex
            ) {
              return prev;
            }
            return null;
          });
        }
      })
      .catch((e) => {
        console.error("getEventModelTimes failed:", e);
        if (!cancelled) {
          setOccurrences([]);
          setOccurrencesLoading(false);
          setOccurrencesError(String(e?.message ?? e ?? getTigerResource("TIGER_LOAD_EVENT_TIMES_ERROR")));
        }
      });
    return () => { cancelled = true; };
  }, [isMultiTime, eventModel, turbine]);

  // Seed state from editingSeries when the dialog opens in multiTime edit mode.
  useEffect(() => {
    if (!open || !isMultiTime || !editingSeries) return;
    setTimeMode(editingSeries.mode === "CURRENT" ? "current" : "historical");
    setEventModel(editingSeries.eventModel);
    if (editingSeries.turbineState) setTurbineState(editingSeries.turbineState);
    setSelectedOccurrenceIndex(
      editingSeries.mode === "RELATIVE" ? editingSeries.relativeIndex : null,
    );
    if (editingSeries.mode !== "CURRENT") {
      const s = tigerSecondsToDate(editingSeries.startTime);
      const e = tigerSecondsToDate(editingSeries.endTime);
      const d = (x: Date) => x.toISOString().slice(0, 10);
      const t = (x: Date) => x.toISOString().slice(11, 19);
      setStartDate(d(s));
      setStartTime(t(s));
      setEndDate(d(e));
      setEndTime(t(e));
    }
  }, [open, isMultiTime, editingSeries]);

  const channels = allChannels.filter((ch) => {
    const tagLower = tagPattern.toLowerCase();
    const descLower = descriptionPattern.toLowerCase();
    if (tagLower && !ch.tag.toLowerCase().includes(tagLower)) return false;
    if (descLower && !ch.description.toLowerCase().includes(descLower)) return false;
    return true;
  });
  const channelColumns = [
    { key: "tag", label: getTigerResource("TIGER_EXPORT_CHANNEL_NAME"), width: 200 },
    { key: "description", label: getTigerResource("TIGER_EXPORT_CHANNEL_DESCR"), width: 340 },
  ];

  const handleRowClick = (idx: number, e: React.MouseEvent) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (e.ctrlKey || e.metaKey) {
        if (next.has(idx)) next.delete(idx); else next.add(idx);
      } else if (e.shiftKey && prev.size > 0) {
        const anchor = Math.min(...prev);
        const [from, to] = idx < anchor ? [idx, anchor] : [anchor, idx];
        for (let i = from; i <= to; i++) next.add(i);
      } else {
        return new Set([idx]);
      }
      return next;
    });
  };

  // After a historical plot with a manually-entered range is applied, prepend
  // its (start, end) to the user-saved Time-mode list so the user can re-pick
  // the same window for follow-up plots without retyping. Persists across
  // event-dropdown switches and across windows via localStorage. RELATIVE
  // plots already came from a model's occurrences list, so don't duplicate
  // them here. CURRENT plots have no anchored window.
  const recordHistoricalRange = (spec: SeriesSpec) => {
    if (spec.mode !== "HISTORICAL") return;
    if (!(spec.startTime > 0) || !(spec.endTime > spec.startTime)) return;
    setUserTimeRanges((prev) => {
      const dup = prev.some((r) => r.start === spec.startTime && r.end === spec.endTime);
      if (dup) return prev;
      return [{ start: spec.startTime, end: spec.endTime }, ...prev];
    });
    if (eventModel === "") {
      setSelectedOccurrenceIndex(0);
    }
  };

  const handleAdd = () => {
    selectedIndices.forEach((idx) => {
      if (idx >= 0 && idx < channels.length) {
        if (isMultiTime) {
          const spec = buildSpec(channels[idx], turbine);
          if (spec) {
            onApplySeries?.(spec);
            recordHistoricalRange(spec);
          }
        } else {
          onApply?.({ action: "add", channel: channels[idx], config_name: turbine, turbine_state: turbineState });
        }
      }
    });
  };


  if (!open) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Turbine Selection */}
          <div style={styles.section}>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE")}</label>
                  <select style={styles.select} value={turbine} onChange={(e) => setTurbine(e.target.value)}>
                    {turbines.map((t) => (
                      <option key={t.config_name} value={t.config_name}>{t.turbine_name}</option>
                    ))}
                  </select>
                </div>
                {mode !== "event" && !hideTurbineState && (
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE_STATE")}</label>
                    <select style={styles.select} value={turbineState} onChange={(e) => setTurbineState(e.target.value)}>
                      {turbineStates.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pattern Matching */}
          <div style={styles.section}>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                {mode !== "event" && (
                  <div style={styles.fieldNarrow}>
                    <label style={styles.fieldLabel}>{getTigerResource("TIGER_CHANNEL_TYPE")}</label>
                    <select style={styles.select} value={channelType} onChange={(e) => setChannelType(e.target.value)}>
                      <option value="Analogue">{getTigerResource("TIGER_GRAPH_CHANNEL_ANALOG")}</option>
                      <option value="Digital">{getTigerResource("TIGER_GRAPH_CHANNEL_DIGITAL")}</option>
                      <option value="All">{getTigerResource("TIGER_GRAPH_CHANNEL_ALL")}</option>
                    </select>
                  </div>
                )}
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_CHANNEL_TAG")}</label>
                  <input style={styles.input} type="text" value={tagPattern} onChange={(e) => setTagPattern(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_CHANNEL_DESCR")}</label>
                  <input style={styles.input} type="text" value={descriptionPattern} onChange={(e) => setDescriptionPattern(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Channel List */}
          <div
            style={
              isMultiTime
                ? { ...styles.section, flex: "0 0 240px", minHeight: 0 }
                : { ...styles.section, flex: 1, minHeight: 0 }
            }
          >
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_GRAPH_CHANNEL_CHANNELS")}</span>
            <div style={{ ...styles.tableWrap, flex: 1, minHeight: 0, display: "flex" }}>
              <div className="list-control" style={{ flex: 1, minHeight: isMultiTime ? 0 : 220, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      {channelColumns.map((col) => (
                        <th key={col.key} style={col.width ? { width: col.width } : undefined}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((ch, idx) => (
                      <tr
                        key={idx}
                        className={selectedIndices.has(idx) ? "selected" : ""}
                        onClick={(e) => handleRowClick(idx, e)}
                        onDoubleClick={() => {
                          if (isMultiTime) {
                            const spec = buildSpec(ch, turbine);
                            if (spec) {
                              onApplySeries?.(spec);
                              recordHistoricalRange(spec);
                            }
                          } else {
                            onApply?.({ action: "add", channel: ch, config_name: turbine, turbine_state: turbineState });
                          }
                        }}
                      >
                        <td>{ch.tag}</td>
                        <td>{ch.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {isMultiTime && (
            <div style={styles.section}>
              <div style={styles.card}>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>
                    <input
                      type="radio"
                      name="timeMode"
                      value="historical"
                      aria-label={getTigerResource("TIGER_CHANNEL_HIST")}
                      checked={timeMode === "historical"}
                      onChange={() => setTimeMode("historical")}
                    />
                    {getTigerResource("TIGER_CHANNEL_HIST")}
                  </label>
                  <label style={styles.fieldLabel}>
                    <input
                      type="radio"
                      name="timeMode"
                      value="current"
                      aria-label={getTigerResource("TIGER_CHANNEL_CUR")}
                      checked={timeMode === "current"}
                      onChange={() => setTimeMode("current")}
                    />
                    {getTigerResource("TIGER_CHANNEL_CUR")}
                  </label>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor="mt-event-dd">{getTigerResource("TIGER_CHANNEL_EVENT")}</label>
                    <select
                      id="mt-event-dd"
                      aria-label={getTigerResource("TIGER_CHANNEL_EVENT")}
                      style={styles.select}
                      value={eventModel}
                      onChange={(e) => setEventModel(e.target.value)}
                    >
                      <option value="">{getTigerResource("TIGER_GRAPH_TIME")}</option>
                      {eventModels.map(([id, name]) => (
                        <option key={id} value={id}>{name || id}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={styles.fieldGroup}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor="mt-start-time">{getTigerResource("TIGER_RES_START_TIME")}</label>
                    <input
                      id="mt-start-time"
                      aria-label={getTigerResource("TIGER_RES_START_TIME")}
                      type="time"
                      step="1"
                      disabled={historicalDisabled}
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                    <LangDateInput
                      aria-label={getTigerResource("TIGER_RES_START_DATE")}
                      disabled={historicalDisabled}
                      value={startDate}
                      onChange={setStartDate}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor="mt-end-time">{getTigerResource("TIGER_RES_END_TIME")}</label>
                    <input
                      id="mt-end-time"
                      aria-label={getTigerResource("TIGER_RES_END_TIME")}
                      type="time"
                      step="1"
                      disabled={historicalDisabled}
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                    <LangDateInput
                      aria-label={getTigerResource("TIGER_RES_END_DATE")}
                      disabled={historicalDisabled}
                      value={endDate}
                      onChange={setEndDate}
                    />
                  </div>
                </div>
                {occurrencesLoading && (
                  <div style={{ padding: 8, color: "#666" }}>{getTigerResource("TIGER_LOAD_EVENT_TIMES_LOADING")}</div>
                )}
                {!occurrencesLoading && occurrencesError && (
                  <div role="alert" style={{ padding: 8, color: "#b00020" }}>
                    {tigerFormat(getTigerResource("TIGER_LOAD_EVENT_TIMES_FAILED"), occurrencesError)}
                  </div>
                )}
                <div
                  aria-label={getTigerResource("TIGER_GRAPH_CHANNEL_EVENT_OCCURRENCES")}
                  style={
                    historicalDisabled
                      ? { pointerEvents: "none", opacity: 0.5 }
                      : undefined
                  }
                >
                    <ListControl<{ start: number; end: number }>
                      columns={[
                        { key: "startTime", label: getTigerResource("TIGER_RES_START_TIME") },
                        { key: "endTime", label: getTigerResource("TIGER_RES_END_TIME") },
                        serialEnabled
                          ? { key: "serial", label: getTigerResource("TIGER_RES_SERIAL") }
                          : { key: "duration", label: getTigerResource("TIGER_RES_DURATION") },
                      ]}
                      items={displayedOccurrences}
                      selectedIndex={selectedOccurrenceIndex ?? -1}
                      height={180}
                      onSelect={(i) => {
                        setSelectedOccurrenceIndex(i);
                        const o = displayedOccurrences[i];
                        if (!o) return;
                        const s = tigerSecondsToDate(o.start);
                        const e = tigerSecondsToDate(o.end);
                        const d = (x: Date) => `${x.getUTCFullYear()}-${pad2(x.getUTCMonth() + 1)}-${pad2(x.getUTCDate())}`;
                        const t = (x: Date) => `${pad2(x.getUTCHours())}:${pad2(x.getUTCMinutes())}:${pad2(x.getUTCSeconds())}`;
                        setStartDate(d(s));
                        setStartTime(t(s));
                        setEndDate(d(e));
                        setEndTime(t(e));
                      }}
                      getRow={(o) => ({
                        startTime: formatTigerDateTime(o.start),
                        endTime: formatTigerDateTime(o.end),
                        duration: formatDurationSecs(o.end - o.start),
                        serial: serialFor(o.start),
                      })}
                    />
                  </div>
                {timeError && (
                  <div role="alert" style={{ color: "red" }}>{timeError}</div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            className="btn-acrylic-primary"
            onClick={handleAdd}
            disabled={selectedIndices.size === 0}
          >
            {getTigerResource("TIGER_RES_ADD")}
          </button>
          <button className="btn-acrylic-secondary" onClick={onClose}>
            {getTigerResource("TIGER_RES_CLOSE")}
          </button>
        </div>
      </div>
    </div>
  );
}
